/**
 * useCategoryLocalize — small hook that returns a function for resolving
 * a category's display name in the active site language.
 *
 * Usage:
 *   const localizeCat = useCategoryLocalize();
 *   <span>{localizeCat(article.categorySlug, article.category)}</span>
 *
 * Falls back to the Arabic name (or the provided fallback) when the
 * category has no English translation or the site language is Arabic.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { categoriesStore, type Category } from "@/store/categoriesStore";
import { onRealtimeEvent } from "@/lib/realtimeManager";

export function useCategoryLocalize() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "ar";
  const [cats, setCats] = useState<Category[]>([]);

  useEffect(() => {
    let cancelled = false;
    categoriesStore.getAll().then((data) => {
      if (!cancelled) setCats(data);
    });
    const unsub = onRealtimeEvent("alsiyahiah:categories:updated", () => {
      categoriesStore.getAll().then((data) => {
        if (!cancelled) setCats(data);
      });
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // bySlug for O(1) lookup
  const bySlug = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of cats) m.set(c.slug, c);
    return m;
  }, [cats]);

  // byArabicName — articles older than this feature were stored with
  // category as the Arabic name, not the slug. Fall back to matching that.
  const byArabicName = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of cats) m.set(c.name, c);
    return m;
  }, [cats]);

  return useCallback(
    (slug: string | undefined | null, fallback: string): string => {
      const cat = (slug && bySlug.get(slug)) || byArabicName.get(fallback);
      if (lang === "en" && cat?.nameEn && cat.nameEn.trim()) return cat.nameEn;
      // Even in AR, prefer the canonical category.name in case the fallback
      // was a stale legacy value
      return cat?.name || fallback;
    },
    [bySlug, byArabicName, lang]
  );
}
